import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { BaseCanvas } from "@/components/canvas/components/BaseCanvas";
import { useInitBaseCanvas } from "@/components/canvas/hooks/useInitBaseCanvas";
import { CanvasBar } from "@/components/canvas/components/CanvasBar";
import { createTextExtension } from "@/components/canvas/extensions/textExtension";
import { useObjectSelectionChangeHandlers } from "@/components/canvas/hooks/useObjectSelectionChangeHandlers";
import { createObjectSelectionExtension } from "@/components/canvas/extensions/objectSelectionExtension";
import { useDeselectObjectHandler } from "@/components/canvas/hooks/useDeselectObjectHandler";
import { useTextAddingAndModification } from "@/components/canvas/hooks/useTextAddingAndModification";
import { createObjectDeletionExtension } from "@/components/canvas/extensions/objectDeletionExtension";
import { useObjectDeleteHandler } from "@/components/canvas/hooks/useObjectDeleteHandler";
import { createViewportInitializerExtension } from "@/components/canvas/extensions/viewportInitializerExtension";
import { useEffect, useRef } from "react";
import { useCanvasAndViewportDimensions } from "@/components/canvas/hooks/useCanvasAndViewportDimensions";
import { useTranslation } from "react-i18next";
import { createDataSaverExtension } from "@/components/canvas/extensions/dataSaverExtension";
import { FabricData } from "@/components/canvas/types/canvasTypes";
import { useDispatch } from "react-redux";
import {
  setCurrentCanvasData,
  setCurrentTemplate,
  setTemplateHasChanges,
} from "@/store/templateEditor/templateEditorSlice";
import { createBackgroundExtension } from "@/components/canvas/extensions/backgroundExtension";
import { useBackgroundModification } from "@/components/canvas/hooks/useBackgroundModification";
import { createCanvasInitializerExtension } from "@/components/canvas/extensions/canvasInitializerExtension";
import { Template } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ExtendedObject } from "@/components/canvas/types/objectTypes";
import {
  findBackgroundImageObjectInFabricJson,
  findViewportObjectInFabricJson,
} from "@/components/canvas/utils/canvasUtils";
import { setBackgroundOptions, setImageOptions, setTextOptions } from "@/store/canvas/canvasSlice";
import {
  returnBackgroundColorOrDefault,
  returnBackgroundImageOpacityOrDefault,
  returnImageUrlOrDefault,
  returnImageFillModeOrDefault,
} from "@/components/canvas/helpers/ackgroundHelpers";
import { ROUTES } from "@/constants/routeConstants";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getCheckeredBg } from "@/lib/tailwindUtils";
import { createLayerManagerExtension } from "@/components/canvas/extensions/layerManagerExtension";
import { useObjectLayerChangeHandler } from "@/components/canvas/hooks/useObjectLayerChangeHandler";
import { createImageExtension } from "@/components/canvas/extensions/imageExtension";
import { useImageAddingAndModification } from "@/components/canvas/hooks/useImageAddingAndModification";
import { showSuccess, showApiError } from "@/lib/toast";
import { updateStockTemplate } from "@/sideEffects/api";
import { useMutation } from "@tanstack/react-query";
import { UpdateStockTemplateNameInput } from "./UpdateStockTemplateNameInput";
import { Inspector } from "@/pages/template/editor/inspector/Inspector";
import { UpdateStockTemplateCategoryNameInput } from "./UpdateStockTemplateCategoryNameInput";

interface TemplateEditorProps {
  template: Template;
}

export const TemplateStockEditor: React.FC<TemplateEditorProps> = ({ template }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { baseCanvasRef, canvas } = useInitBaseCanvas();
  const { handleObjectSelection, handleObjectDeselection } = useObjectSelectionChangeHandlers();
  const { initDeselectFunction } = useDeselectObjectHandler();
  const { handleTextOptionsUpdate } = useTextAddingAndModification(baseCanvasRef);
  const { handleImageOptionsUpdate } = useImageAddingAndModification(baseCanvasRef);
  const { initDeleteActiveObjectFunction, handleObjectDeleting } = useObjectDeleteHandler();
  const { initCanvasAndViewportUpdateDimensionsFunction, viewportSettingsRef } = useCanvasAndViewportDimensions();
  const { handleViewportModification, initSetAsBackgroundImageFunction } = useBackgroundModification(baseCanvasRef);
  const { initChangeLayerFunction } = useObjectLayerChangeHandler();

  useBreadcrumbs([
    { path: ROUTES.TEMPLATE_STOCK, name: t("entities.templateStock") },
    {
      path: ROUTES.TEMPLATE_STOCK_EDITOR,
      name: template?.name || "",
      component: (
        <div className="flex items-center gap-2">
          <UpdateStockTemplateNameInput templateId={template.id} name={template.name} />
          <Tooltip>
            <TooltipTrigger>
              <div className="text-sm text-muted-foreground">({template.viewport_size})</div>
            </TooltipTrigger>
            <TooltipContent>{t("template.viewportSize")}</TooltipContent>
          </Tooltip>
          <Separator orientation="vertical" style={{ height: 20}} />
          <UpdateStockTemplateCategoryNameInput templateId={template.id} categoryName={template.categoryName ?? ""} />
        </div>
      ),
    },
  ]);

  // Update 'background options' when we land in the template editor, since there is no way to click to background in canvas
  useEffect(() => {
    const backgroundImage = template.canvas_data
      ? (findBackgroundImageObjectInFabricJson(template.canvas_data) as ExtendedObject & {
          src: string;
        }) ?? null
      : null;
    const viewPort = template.canvas_data ? findViewportObjectInFabricJson(template.canvas_data) ?? null : null;

    dispatch(
      setBackgroundOptions({
        backgroundColor: returnBackgroundColorOrDefault(viewPort?.fill),
        imageOpacity: returnBackgroundImageOpacityOrDefault(backgroundImage?.opacity),
        imageUrl: returnImageUrlOrDefault(backgroundImage?.src),
        fillMode: returnImageFillModeOrDefault(backgroundImage?.backgroundImageFillMode),
      })
    );
  }, [template, dispatch]);

  useEffect(() => {
    return function cleanup() {
      dispatch(setCurrentTemplate(null));
      dispatch(setBackgroundOptions(null));
      dispatch(setImageOptions(null));
      dispatch(setTextOptions(null));
    };
  }, [dispatch]);

  const handleFabricJsStateUpdate = (data: FabricData) => {
    dispatch(setCurrentCanvasData(data));
  };

  const updateCurrentCanvasData = (data: FabricData) => {
    dispatch(setCurrentCanvasData(data));
  };

  const updateTemplateMutation = useMutation({
    mutationFn: (data: FabricData) =>
      updateStockTemplate({
        id: template.id,
        firmGuid: template.firmGuid ?? "",
        name: template.name,
        viewport_size: template.viewport_size,
        canvas_data: JSON.stringify(data),
        categoryName: template.categoryName ?? "",
      }),
    onSuccess: (data) => {
      showSuccess(t("common.updatedSuccess"));
      dispatch(setCurrentCanvasData(JSON.parse(data.data.canvasData)));
      dispatch(setTemplateHasChanges(false));
    },
    onError: showApiError,
  });

  return (
    <div
      className="flex border-t"
      style={{
        height: "calc(100vh - 120px)",
      }}
    >
      <div className="flex-1 h-full">
        {canvas && <CanvasBar canvas={canvas} />}
        <div ref={wrapperRef} className={cn(getCheckeredBg(), "h-full")}>
          <BaseCanvas
            ref={baseCanvasRef}
            canvas={canvas}
            extensions={[
              createDataSaverExtension({
                handleUpdate: handleFabricJsStateUpdate,
                handleImmediateUpdate: updateCurrentCanvasData,
              }),
              createViewportInitializerExtension({
                canvasWrapperElementRef: wrapperRef,
                viewportSettingsRef,
                recalculateCanvasAndViewportDimensionsFunctionFactory: initCanvasAndViewportUpdateDimensionsFunction,
              }),
              createCanvasInitializerExtension({
                initialData: template.canvas_data,
              }),
              createTextExtension({
                handleOptionsUpdate: handleTextOptionsUpdate,
              }),
              createImageExtension({
                handleOptionsUpdate: handleImageOptionsUpdate,
              }),
              createObjectSelectionExtension({
                handleSelection: ({ selected }) => {
                  handleObjectSelection(selected);
                },
                handleDeselection: handleObjectDeselection,
                deselectFunctionFactory: initDeselectFunction,
              }),
              createObjectDeletionExtension({
                deleteActiveObjectFunctionFactory: initDeleteActiveObjectFunction,
                handleObjectDeleting,
              }),
              createBackgroundExtension({
                handleViewportModification,
                setImageAsBackgroundFunctionFactory: initSetAsBackgroundImageFunction,
              }),
              createLayerManagerExtension({
                changeLayerFunctionFactory: initChangeLayerFunction,
              }),
            ]}
          />
        </div>
      </div>
      <Separator orientation="vertical" />
      <div className="w-[350px]">
        <ScrollArea className="h-full">
          <Inspector onClickSave={updateTemplateMutation.mutate} saving={updateTemplateMutation.isPending} />
        </ScrollArea>
      </div>
    </div>
  );
};
